import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Plug, Sparkles, Database, Activity } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { usePermissions } from '../../contexts/AuthContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import WorkspacePill from '../../components/layout/WorkspacePill';
import BusinessFilter from '../../components/layout/BusinessFilter';
import FilterChip from '../../components/ui/FilterChip';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  sourcesApi,
  isPlanCapacityError,
  type OverviewSource,
  type AdapterMode,
} from '../../services/sources-api';
import { schedulesApi } from '../../services/schedules-api';
import { handleApiError } from '../../services/api';
import { monogram, needsAttention } from './sourceDisplay';
import SourceRow, { type DrawerTab } from './SourceRow';
import SourceSettingsDrawer from './SourceSettingsDrawer';
import { PlaidReconnect } from '../../components/integrations/PlaidReconnect';

type TypeFilter = 'all' | 'stored' | 'ondemand';

const HEADER_GRID =
  'hidden lg:grid grid-cols-[minmax(0,2.4fr)_165px_110px_90px_80px_70px] gap-3 px-4 pt-2 pb-1.5';

const DataSourcesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManageAccounts } = usePermissions();
  const isAdmin = canManageAccounts();

  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  // `?org=` lets other pages (home roll-up) land on one business without a
  // context switch. Filter state itself stays local and non-persisted (§10.4).
  const [searchParams] = useSearchParams();
  const [orgFilter, setOrgFilter] = useState<string | null>(searchParams.get('org'));
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [drawer, setDrawer] = useState<{ source: OverviewSource; orgId: string; orgName: string; tab: DrawerTab } | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['sourcesOverview'],
    queryFn: () => sourcesApi.getOverview(),
  });

  const { data: entitlements } = useEntitlements();
  // Free slot in the workspace's pooled source limit. The server re-checks this
  // under a lock; this only decides whether we offer "Turn back on" or send the
  // user straight to billing, so a stale read costs at most one 402/403 hop.
  const hasSourceCapacity = entitlements
    ? entitlements.sources.used < entitlements.sources.limit
    : false;

  const goToBilling = (message: string): void => {
    toast.error(message);
    navigate('/settings/billing');
  };

  const reactivate = useMutation({
    mutationFn: (id: string) => sourcesApi.reactivateSource(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      void queryClient.invalidateQueries({ queryKey: ['billing', 'entitlements'] });
      toast.success('Source is back on — syncing resumes shortly');
    },
    onError: (e) => {
      if (isPlanCapacityError(e)) {
        goToBilling(handleApiError(e) || 'Your plan has no free data source slot.');
        return;
      }
      toast.error(handleApiError(e) || 'Could not turn the source back on');
    },
  });

  const handleReactivate = (source: OverviewSource): void => {
    if (!hasSourceCapacity) {
      goToBilling("Your plan's data sources are all in use. Add a slot or upgrade to turn this one back on.");
      return;
    }
    reactivate.mutate(source.id);
  };

  const toggle = useMutation({
    mutationFn: ({ id, field, value }: { id: string; field: 'syncEnabled' | 'aiAccessEnabled'; value: boolean }) =>
      sourcesApi.updateSource(id, { [field]: value }),
    onSuccess: (_res, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      if (vars.field === 'aiAccessEnabled') {
        toast.success(vars.value ? 'AI access resumed' : 'AI access paused — takes effect immediately');
      }
    },
    onError: (e) => toast.error(handleApiError(e) || 'Update failed'),
  });

  const syncNow = useMutation({
    mutationFn: (id: string) => schedulesApi.triggerManualSync(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
      toast.success('Sync started');
    },
    onError: (e) => toast.error(handleApiError(e) || 'Sync failed'),
  });

  const [reconnecting, setReconnecting] = useState<OverviewSource | null>(null);

  const handleReconnectDone = React.useCallback(
    (reconnected: boolean) => {
      setReconnecting(null);
      if (reconnected) void queryClient.invalidateQueries({ queryKey: ['sourcesOverview'] });
    },
    [queryClient],
  );

  const matchesType = (mode: AdapterMode): boolean =>
    typeFilter === 'all' || (typeFilter === 'stored' ? mode === 'ingestion' : mode !== 'ingestion');

  const filteredOrgs = useMemo(() => {
    if (!data) return [];
    return data.organizations
      .filter((o) => !orgFilter || o.id === orgFilter)
      .map((o) => ({
        ...o,
        sources: o.sources.filter(
          (s) => matchesType(s.mode) && (!attentionOnly || needsAttention(s)),
        ),
      }))
      .filter((o) => o.sources.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, typeFilter, orgFilter, attentionOnly]);

  const attentionSource = data?.organizations
    .flatMap((o) => o.sources)
    .find((s) => needsAttention(s));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 pt-7 pb-24">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Data Sources</h1>
        <div className="flex items-center gap-2.5">
          <WorkspacePill />
          {isAdmin && (
            <button
              onClick={() => navigate('/sources/integrations')}
              className="inline-flex items-center gap-2 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              <Plus className="w-4 h-4" /> Connect a source
            </button>
          )}
        </div>
      </div>

      {isError && (
        <div className="mt-6 rounded-lg border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-foreground">
          Couldn&apos;t load your sources — try refreshing.
        </div>
      )}

      {data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5 mt-6">
            <Kpi
              icon={<Plug className="w-4 h-4" />}
              label="Connected sources"
              value={String(data.totals.sources)}
              caption={
                data.totals.inactive > 0
                  ? `${data.totals.inactive} switched off — plan limit`
                  : `across ${data.organizations.length} ${data.organizations.length === 1 ? 'business' : 'businesses'}`
              }
              captionWarn={data.totals.inactive > 0}
            />
            <Kpi
              icon={<Sparkles className="w-4 h-4" />}
              label="AI queries · 30 days"
              value={data.totals.queries30d.toLocaleString()}
              caption="chat + external AI tools"
            />
            <Kpi
              icon={<Database className="w-4 h-4" />}
              label="New records today"
              value={data.totals.recordsToday.toLocaleString()}
              caption="stored sources only"
            />
            <Kpi
              icon={<Activity className="w-4 h-4" />}
              label="Needs attention"
              value={String(data.totals.needsAttention)}
              caption={attentionSource ? attentionSource.name : 'all healthy'}
              warn={data.totals.needsAttention > 0}
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2.5 mt-5">
            <FilterChip
              label={typeFilter === 'all' ? 'All types' : typeFilter === 'stored' ? 'Stored copy' : 'On-demand'}
              options={[
                { id: 'all', label: 'All types' },
                { id: 'stored', label: 'Stored copy' },
                { id: 'ondemand', label: 'On-demand' },
              ]}
              selected={typeFilter}
              onSelect={(id) => setTypeFilter(id as TypeFilter)}
            />
            <BusinessFilter
              value={orgFilter}
              onChange={setOrgFilter}
              orgs={data.organizations.map((o) => ({ id: o.id, name: o.name }))}
            />
            {data.totals.needsAttention > 0 && (
              <button
                onClick={() => setAttentionOnly((v) => !v)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-[12.5px] font-semibold',
                  attentionOnly
                    ? 'bg-warn/20 border-warn/40 text-warn'
                    : 'bg-warn/10 border-warn/25 text-warn hover:bg-warn/20',
                )}
              >
                <Activity className="w-3.5 h-3.5" /> Needs attention · {data.totals.needsAttention}
              </button>
            )}
          </div>

          {/* Org sections */}
          {filteredOrgs.length === 0 && (
            <div className="mt-10 rounded-lg border border-dashed border-border bg-card px-6 py-16 text-center">
              <p className="text-sm font-medium text-foreground">
                {data.totals.sources === 0 ? 'No data sources connected yet' : 'Nothing matches these filters'}
              </p>
              {data.totals.sources === 0 && isAdmin && (
                <button
                  onClick={() => navigate('/sources/integrations')}
                  className="mt-4 inline-flex items-center gap-2 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  <Plus className="w-4 h-4" /> Connect your first source
                </button>
              )}
            </div>
          )}

          {filteredOrgs.map((org) => (
            <section key={org.id} className="mt-7">
              <div className="flex items-center gap-2.5 mb-2.5">
                <div className="w-7 h-7 rounded-lg bg-primary/15 text-primary flex items-center justify-center text-[11px] font-bold">
                  {monogram(org.name)}
                </div>
                <span className="text-[15px] font-semibold text-foreground">{org.name}</span>
                <span className="text-[12.5px] text-muted-foreground">
                  · {org.sources.length} {org.sources.length === 1 ? 'source' : 'sources'}
                </span>
              </div>
              <div className="rounded-[10px] border border-border bg-card shadow-sm overflow-hidden">
                <div className={HEADER_GRID}>
                  {['Source', 'Sync', 'AI access', 'Queries · 30d', 'People', ''].map((h, i) => (
                    <div key={i} className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {h}
                    </div>
                  ))}
                </div>
                {org.sources.map((source) => (
                  <SourceRow
                    key={source.id}
                    source={source}
                    isAdmin={isAdmin}
                    hasSourceCapacity={hasSourceCapacity}
                    reactivating={reactivate.isPending && reactivate.variables === source.id}
                    syncing={syncNow.isPending && syncNow.variables === source.id}
                    onOpenDrawer={(s, tab) => setDrawer({ source: s, orgId: org.id, orgName: org.name, tab })}
                    onToggle={(s, field, value) => toggle.mutate({ id: s.id, field, value })}
                    onSyncNow={(s) => syncNow.mutate(s.id)}
                    onReconnect={setReconnecting}
                    onReactivate={handleReactivate}
                  />
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {reconnecting && (
        <PlaidReconnect
          key={reconnecting.id}
          dataSourceId={reconnecting.id}
          institutionName={reconnecting.name}
          onDone={handleReconnectDone}
        />
      )}

      <SourceSettingsDrawer
        source={drawer?.source ?? null}
        orgId={drawer?.orgId ?? null}
        orgName={drawer?.orgName ?? null}
        initialTab={drawer?.tab ?? 'general'}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
};

const Kpi: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  caption: string;
  warn?: boolean;
  captionWarn?: boolean;
}> = ({ icon, label, value, caption, warn, captionWarn }) => (
  <div className="rounded-[10px] border border-border bg-card shadow-sm p-4">
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
      {icon} {label}
    </div>
    <div className={clsx('text-2xl font-semibold mt-2', warn ? 'text-warn' : 'text-foreground')}>{value}</div>
    <div
      className={clsx(
        'text-[11px] mt-0.5 truncate',
        captionWarn ? 'text-warn font-semibold' : 'text-muted-foreground',
      )}
    >
      {caption}
    </div>
  </div>
);

export default DataSourcesPage;
